import type {
  PlayerStatsAggregation,
  PlayerStatsVenue
} from '@api-service/player-stats';

export type FormWindow = 'season' | 'last6' | 'last3';
export type StatsBasis = 'per90' | 'perGame' | 'total';

export type ProfileFilters = {
  season: number;
  venue: PlayerStatsVenue;
  window: FormWindow;
  basis: StatsBasis;
};

export const FIRST_GAMEWEEK = 1;
export const LAST_GAMEWEEK = 38;

/** The season selector offers the current season and the one before it. */
export const SELECTABLE_SEASON_COUNT = 2;

const WINDOW_LENGTH: Record<FormWindow, number | null> = {
  season: null,
  last6: 6,
  last3: 3
};

export const VENUE_OPTIONS: { value: PlayerStatsVenue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'home', label: 'Home' },
  { value: 'away', label: 'Away' }
];

export const WINDOW_OPTIONS: { value: FormWindow; label: string }[] = [
  { value: 'season', label: 'Season' },
  { value: 'last6', label: 'Last 6' },
  { value: 'last3', label: 'Last 3' }
];

export const BASIS_OPTIONS: { value: StatsBasis; label: string }[] = [
  { value: 'per90', label: 'Per 90' },
  { value: 'perGame', label: 'Per game' },
  { value: 'total', label: 'Total' }
];

const BASIS_TO_AGGREGATION: Record<StatsBasis, PlayerStatsAggregation> = {
  per90: 'per90',
  perGame: 'perAppearance',
  total: 'total'
};

export const toAggregation = (basis: StatsBasis): PlayerStatsAggregation =>
  BASIS_TO_AGGREGATION[basis];

/**
 * Totals are whole-ish numbers where a second decimal is noise; rates need two
 * to stay meaningful.
 */
export const decimalsForBasis = (basis: StatsBasis): number =>
  basis === 'total' ? 1 : 2;

/**
 * Resolves the form window to a gameweek range. `latestGameweek` is the last
 * gameweek with results — during a completed season that is 38, and mid-season
 * it is the current gameweek.
 */
export const toGameweekRange = (
  window: FormWindow,
  latestGameweek: number
): { minGameweek: number; maxGameweek: number } => {
  const maxGameweek = Math.min(
    Math.max(latestGameweek, FIRST_GAMEWEEK),
    LAST_GAMEWEEK
  );
  const length = WINDOW_LENGTH[window];

  if (length === null) {
    return { minGameweek: FIRST_GAMEWEEK, maxGameweek };
  }

  return {
    minGameweek: Math.max(FIRST_GAMEWEEK, maxGameweek - length + 1),
    maxGameweek
  };
};

/**
 * Reads the `season` query parameter, falling back when it is absent or not a
 * usable season.
 *
 * The coercion is deliberately explicit: `Number(null)` is `0`, and `0` passes
 * `Number.isFinite`, so coercing first would pin the season to `0` whenever the
 * parameter is missing. Zero then reads as "before detailed Opta data existed"
 * and silently hides every season-gated module on the default view.
 */
export const parseSeasonParam = (
  raw: string | null | undefined,
  fallback: number
): number => {
  if (raw === null || raw === undefined || raw.trim() === '') return fallback;

  const parsed = Number(raw);
  const isUsableSeason =
    Number.isInteger(parsed) && parsed >= EARLIEST_SELECTABLE_SEASON;

  return isUsableSeason ? parsed : fallback;
};

/**
 * Floor for a season the API will accept. Its schema rejects anything before
 * 2018, so a value below this can only be a malformed or stale URL.
 */
export const EARLIEST_SELECTABLE_SEASON = 2018;

/** Renders a season start year as the FPL-style label, e.g. 2026 to "2026/27". */
export const toSeasonLabel = (season: number): string =>
  `${season}/${((season + 1) % 100).toString().padStart(2, '0')}`;

export const getSelectableSeasons = (currentSeason: number): number[] =>
  Array.from(
    { length: SELECTABLE_SEASON_COUNT },
    (_unused, index) => currentSeason - index
  );

const VENUE_DESCRIPTION: Record<PlayerStatsVenue, string> = {
  all: 'all fixtures',
  home: 'home fixtures',
  away: 'away fixtures'
};

const BASIS_DESCRIPTION: Record<StatsBasis, string> = {
  per90: 'per 90 minutes',
  perGame: 'per appearance',
  total: 'totals'
};

/** Subtitle describing exactly what the numbers on screen are filtered to. */
export const describeFilters = (
  filters: ProfileFilters,
  gameweekCount: number
): string =>
  [
    toSeasonLabel(filters.season),
    VENUE_DESCRIPTION[filters.venue],
    `${gameweekCount} ${gameweekCount === 1 ? 'gameweek' : 'gameweeks'}`,
    BASIS_DESCRIPTION[filters.basis]
  ].join(' · ');
